import React, { useEffect } from 'react';
import { initInstana, instanaTrackPage } from '@bizzabo/instana-api';
import { ConsentCategory, onCategoryAllowed } from '../utils/consentHelper';

/**
 * Consent-gated replacement for `@bizzabo/instana`'s `withInstanaPageTracking`.
 *
 * The upstream HOC calls `initInstana` unconditionally, which injects
 * `//eum.instana.io/eum.min.js` via a plain `document.createElement('script')` —
 * a tag that carries no `optanon-category` class and no `type`, so neither
 * OneTrust nor the homegrown script gate can block it. On pages served by
 * settings.hbs (settings / changeEmail) it is the *only* Instana loader, so
 * Instana loaded regardless of consent.
 *
 * Here the injection is deferred until the Performance (C0002) category is
 * allowed. On pages that already ship the `instanaAgent.hbs` tag (which OneTrust
 * gates correctly on its own) we wait for that agent instead of injecting a
 * second copy — the upstream HOC raced it and loaded the agent twice.
 */
const EUM_SCRIPT_SELECTOR = 'script[src*="eum.min.js"]';
const AGENT_POLL_INTERVAL = 200;
const AGENT_POLL_MAX_ATTEMPTS = 50; // give the hbs agent ~10s to come up

const withInstanaPageTracking = ({ page, instanaKey }) => Comp => props => {
    useEffect(() => {
        if (window.ineum) {
            instanaTrackPage(page);
            return undefined;
        }

        let pollId = null;
        const trackWhenAgentReady = () => {
            let attempts = 0;
            pollId = setInterval(() => {
                attempts += 1;
                if (window.ineum) {
                    instanaTrackPage(page);
                } else if (attempts < AGENT_POLL_MAX_ATTEMPTS) {
                    return;
                }
                clearInterval(pollId);
                pollId = null;
            }, AGENT_POLL_INTERVAL);
        };

        const stopWaitingForConsent = onCategoryAllowed(ConsentCategory.Performance, () => {
            if (window.ineum) {
                instanaTrackPage(page);
            } else if (document.querySelector(EUM_SCRIPT_SELECTOR)) {
                // instanaAgent.hbs owns the agent on this page; OneTrust just
                // un-blocked its tag, so wait for it rather than double-loading.
                trackWhenAgentReady();
            } else {
                initInstana(instanaKey, () => instanaTrackPage(page));
            }
        });

        return () => {
            stopWaitingForConsent();
            if (pollId) {
                clearInterval(pollId);
            }
        };
        // `page`/`instanaKey` are stable HOC-factory args — run once on mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <Comp { ...props } />;
};

export default withInstanaPageTracking;
export { withInstanaPageTracking };
